import { csvParse } from "d3-dsv";
import { formatLocale } from "d3-format";
// import { debounce } from "throttle-debounce"; // TF BUG: THIS CAUSES PROBLEMS WITH SERVER SIDE RENDERING
// import { patchMapboxOpenmaptilesLanguages } from "./map/mapbox-helpers";

// FORMATTING
export const px = (str) => `${str}px`;
export const deg = (str) => `${str}deg`;
export const percent = (str) => `${str}%`;
export const celcius = (str) => `${str} °C`;

export const toObject = (arr, idKey = "id") => arr.reduce((obj, item) => ({ ...obj, [item[idKey]]: item }), {});

export const round = (v, digits) => Math.round(v * 10 ** digits) / 10 ** digits;

export const createNumberArray = (start, end) => [...Array(end - start + 1).keys()].map((x) => x + start);

export const getChangeInPercent = (value, baselineValue) => (100 * (value - baselineValue)) / baselineValue;

export const mod = (n, m) => {
	return ((n % m) + m) % m;
};

const pi = Math.PI;
const tau = 2 * pi;

export const extractProperties = (geojson) => geojson?.features.map((f) => f.properties);

export const rad = (deg) => (deg / 360) * tau;

const linear = (t, b, c, d) => (c * t) / d + b;

export function isInViewport(el) {
	const rect = el.getBoundingClientRect();
	return (
		rect.top >= 0 &&
		rect.left >= 0 &&
		rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
		rect.right <= (window.innerWidth || document.documentElement.clientWidth)
	);
}

export function animate(from, to, duration, update) {
	let start = null;
	const change = to - from;

	function loop() {
		const timestamp = +new Date();
		start = !start ? timestamp : start;
		const progress = timestamp - start;

		update(linear(progress, from, change, duration));

		if (progress < duration) {
			window.requestAnimationFrame(loop);
		}
	}

	window.requestAnimationFrame(loop);
}

export function getPositionRelativeToParent(parent, child) {
	const parentPos = parent.getBoundingClientRect();
	const childPos = child.getBoundingClientRect();
	const relativePos = {};

	relativePos.top = childPos.top - parentPos.top;
	relativePos.right = childPos.right - parentPos.right;
	relativePos.bottom = childPos.bottom - parentPos.bottom;
	relativePos.left = childPos.left - parentPos.left;

	return relativePos;
}

function fallbackCopyTextToClipboard(text) {
	const textArea = document.createElement("textarea");
	textArea.value = text;

	// Avoid scrolling to bottom
	textArea.style.top = "0";
	textArea.style.left = "0";
	textArea.style.position = "fixed";

	document.body.appendChild(textArea);
	textArea.focus();
	textArea.select();

	document.execCommand("copy");
	document.body.removeChild(textArea);
}

export function copyTextToClipboard(text) {
	if (!navigator.clipboard) {
		fallbackCopyTextToClipboard(text);
		return;
	}
	navigator.clipboard.writeText(text);
}

export function scrollTo(elem, offset = 0, returnPromise) {
	const tBody = document.documentElement.getBoundingClientRect().top;
	const tE = elem.getBoundingClientRect().top;
	const t = tE - tBody + offset;
	window.scrollTo({ top: t, behavior: "smooth" });
	return returnPromise
		? new Promise((resolve, reject) => {
				const failed = setTimeout(() => {
					reject();
				}, 2000);

				const scrollHandler = () => {
					if (window.pageYOffset === t) {
						window.removeEventListener("scroll", scrollHandler);
						clearTimeout(failed);
						resolve();
					}
				};
				if (window.pageYOffset === t) {
					clearTimeout(failed);
					resolve();
				} else {
					window.addEventListener("scroll", scrollHandler);
				}
		  })
		: true;
}

export function deepcopy(x) {
	return JSON.parse(JSON.stringify(x));
}

/*
 * RESIZE HELPER
 */

// export function setupResizeHandler(node, breakpoint, handler, debounceTime) {
// 	let w = node.getBoundingClientRect().width;

// 	function check() {
// 		const wNew = node.getBoundingClientRect().width;
// 		if (wNew !== w) {
// 			w = wNew;
// 			handler(w < breakpoint);
// 		}
// 	}

// 	handler(w < breakpoint);

// 	if (debounceTime) {
// 		window.addEventListener("resize", debounce(debounceTime, check));
// 	} else {
// 		window.addEventListener("resize", check);
// 	}

// 	return check;
// }

/*
 * FUNCTIONS TO LOAD SCRIPTS AND STYLES ON DEMAND
 *
 * the promises are cached in `loadingPromises` to prevent loading assets
 * multiple times
 */

const loadingPromises = {};

export function loadScript(url) {
	if (loadingPromises[url]) return loadingPromises[url];
	const p = new Promise((resolve, reject) => {
		const script = document.createElement("script");
		script.onload = () => {
			console.info(`loaded script: ${url}`);
			resolve();
		};
		script.onerror = () => {
			console.err(`failed to load script: ${url}`);
			reject();
		};
		script.src = url;
		document.getElementsByTagName("head")[0].appendChild(script);
	});
	loadingPromises[url] = p;
	return p;
}

export function loadStyle(url) {
	if (loadingPromises[url]) return loadingPromises[url];
	const p = new Promise((resolve, reject) => {
		const link = document.createElement("link");
		link.type = "text/css";
		link.rel = "stylesheet";
		link.onload = () => {
			console.info(`loaded CSS: ${url}`);
			resolve();
		};
		link.onerror = () => {
			console.err(`failed to load CSS: ${url}`);
			reject();
		};
		link.href = url;
		document.getElementsByTagName("head")[0].appendChild(link);
	});
	loadingPromises[url] = p;
	return p;
}

const dataBodies = {};

export function loadCSV(url) {
	if (loadingPromises[url]) {
		return loadingPromises[url].then(() => csvParse(dataBodies[url]));
	}
	const p = fetch(url)
		.then((x) => x.text())
		.then((x) => {
			dataBodies[url] = x;
		});
	loadingPromises[url] = p;
	return p.then(() => csvParse(dataBodies[url]));
}

export function loadJSON(url) {
	if (loadingPromises[url]) {
		return loadingPromises[url].then(() => JSON.parse(dataBodies[url]));
	}
	const p = fetch(url)
		.then((x) => x.text())
		.then((x) => {
			dataBodies[url] = x;
		});
	loadingPromises[url] = p;
	return p.then(() => JSON.parse(dataBodies[url]));
}

export function loadText(url) {
	if (loadingPromises[url]) {
		return loadingPromises[url].then(() => dataBodies[url]);
	}
	const p = fetch(url)
		.then((x) => x.text())
		.then((x) => {
			dataBodies[url] = x;
		});
	loadingPromises[url] = p;
	return p.then(() => dataBodies[url]);
}

const cachePreventString = Math.ceil(+new Date() / (1000 * 60 * 5)).toString(36);

export function loadJSONcp(url) {
	return loadJSON(`${url}?v=${cachePreventString}`);
}

export function loadCSVcp(url) {
	return loadCSV(`${url}?v=${cachePreventString}`);
}

function patchD3() {
	const d3 = window.d3;
	if (d3) {
		// `true` for all Internet Explorer versions (not Edge).
		const ie = "ActiveXObject" in window;

		// extend d3: move to front / back; do nothing on IE, b/c this breaks mouse events(!)
		d3.selection.prototype.moveToFront = function (fct) {
			if (ie) return this;
			return this.each(function (d, i) {
				if (fct === undefined || fct(d, i)) {
					if (this.nextElementSibling) this.parentNode.appendChild(this);
				}
			});
		};
		d3.selection.prototype.moveToBack = function (fct) {
			if (ie) return this;
			return this.each(function (d, i) {
				if (fct === undefined || fct(d, i)) {
					const firstChild = this.parentNode.firstChild;
					if (firstChild) {
						this.parentNode.insertBefore(this, firstChild);
					}
				}
			});
		};
	}
}

export function loadD3() {
	let patched = false;
	return loadScript(`${PATH}vendor/d3.v5.custom.v2.min.js`).then(() => {
		if (!patched) {
			patchD3();
			patched = true;
		}
		return window.d3;
	});
}

export function loadMapbox() {
	let patched = false;
	if (window.mapboxgl) {
		patchMapboxOpenmaptilesLanguages();
		patched = true;
		return Promise.resolve(true);
	}
	return Promise.all([
		loadScript(`${PATH}assets/vendor/mapbox-gl.js`).then(() => {
			if (!patched) {
				patchMapboxOpenmaptilesLanguages();
				patched = true;
			}
		}),
		loadStyle(`${PATH}assets/vendor/mapbox-gl.css`),
	]);
}

export function loadTopojson() {
	return loadScript(`${PATH}assets/vendor/topojson.min.js`).then(() => window.topojson);
}

/*
 * HELPER FUNCTIONS FOR URL PARAMETERS
 * (with IE 11 support)
 */

export function getParameterByName(name, url) {
	const u = url || window.location.href;
	const n = name.replace(/[\[\]]/g, "\\$&");
	const regex = new RegExp(`[?&]${n}(=([^&#]*)|&|#|$)`);
	const results = regex.exec(u);
	if (!results) return null;
	if (!results[2]) return "";
	return decodeURIComponent(results[2].replace(/\+/g, " "));
}

export function getParameters() {
	/*
  const search = window.location.search.substring(1);
  const string =
  `{"${decodeURI(search).replace(/"/g, '\\"').replace(/&/g, '","').replace(/=/g, '":"')}"}`;
  return search ? JSON.parse(string) : {};
  */
	const search = window.location.search.substring(1);
	return JSON.parse(`{"${search.replace(/&/g, '","').replace(/=/g, '":"')}"}`, (key, value) =>
		key === "" ? value : decodeURIComponent(value.replace(/\+/g, " "))
	);
}

export function getParamsString(params) {
	return Object.keys(params)
		.map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
		.join("&");
}

/*
 * NEW HELPERS
 */

export function renderTemplate(template, data) {
	let html = template;
	Object.keys(data).forEach((k) => {
		let cont = true;
		let htmlnew;
		while (cont) {
			htmlnew = html.replace(`{${k}}`, data[k]);
			if (htmlnew === html) cont = false;
			html = htmlnew;
		}
	});
	return html;
}

export function ascending(a, b) {
	return a < b ? -1 : a > b ? 1 : a >= b ? 0 : NaN;
}

export function descending(a, b) {
	return b < a ? -1 : b > a ? 1 : b >= a ? 0 : NaN;
}

/* eslint-disable no-restricted-syntax */
/* eslint-disable no-shadow */
/* eslint-disable no-self-compare */
/* eslint-disable no-param-reassign */
/* eslint-disable no-cond-assign */
export function gmax(values, valueof) {
	let max;
	if (valueof === undefined) {
		for (const value of values) {
			if (value != null && (max < value || (max === undefined && value >= value))) {
				max = value;
			}
		}
	} else {
		let index = -1;
		for (let value of values) {
			if ((value = valueof(value, ++index, values)) != null && (max < value || (max === undefined && value >= value))) {
				max = value;
			}
		}
	}
	return max;
}

export function gmin(values, valueof) {
	let min;
	if (valueof === undefined) {
		for (const value of values) {
			if (value != null && (min > value || (min === undefined && value >= value))) {
				min = value;
			}
		}
	} else {
		let index = -1;
		for (let value of values) {
			if ((value = valueof(value, ++index, values)) != null && (min > value || (min === undefined && value >= value))) {
				min = value;
			}
		}
	}
	return min;
}

export function gmaxindex(values, valueof) {
	let max;
	let maxIndex = -1;
	let index = -1;
	if (valueof === undefined) {
		for (const value of values) {
			++index;
			if (value != null && (max < value || (max === undefined && value >= value))) {
				max = value;
				maxIndex = index;
			}
		}
	} else {
		for (let value of values) {
			if ((value = valueof(value, ++index, values)) != null && (max < value || (max === undefined && value >= value))) {
				max = value;
				maxIndex = index;
			}
		}
	}
	return maxIndex;
}

export function gminindex(values, valueof) {
	let min;
	let minIndex = -1;
	let index = -1;
	if (valueof === undefined) {
		for (const value of values) {
			++index;
			if (value != null && (min > value || (min === undefined && value >= value))) {
				min = value;
				minIndex = index;
			}
		}
	} else {
		for (let value of values) {
			if ((value = valueof(value, ++index, values)) != null && (min > value || (min === undefined && value >= value))) {
				min = value;
				minIndex = index;
			}
		}
	}
	return minIndex;
}

/* eslint-enable no-restricted-syntax */
/* eslint-enable no-shadow */
/* eslint-enable no-self-compare */
/* eslint-enable no-param-reassign */
/* eslint-enable no-cond-assign */

export function makeid(length) {
	let result = "";
	const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
	const charactersLength = characters.length;
	for (let i = 0; i < length; i++) {
		result += characters.charAt(Math.floor(Math.random() * charactersLength));
	}
	return result;
}

export function getNode(nodeOrSelector) {
	return typeof nodeOrSelector === "string" ? document.querySelector(nodeOrSelector) : nodeOrSelector;
}

export function stripHTML(s) {
	return s.replace(/<[^>]*>?/gm, "");
}

export function createElementFromHTML(htmlString) {
	const div = document.createElement("div");
	div.innerHTML = htmlString.trim();
	return div.firstChild;
}

/* eslint-disable no-bitwise */
export function lighten(color, amount) {
	const num = parseInt(color.replace("#", ""), 16);
	const f = 1 - amount;
	const c = 255 * amount;
	const R = Math.round((num >> 16) * f + c);
	const G = Math.round(((num >> 8) & 0x00ff) * f + c);
	const B = Math.round((num & 0x0000ff) * f + c);
	const out = `#${(
		0x1000000 +
		(R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
		(G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
		(B < 255 ? (B < 1 ? 0 : B) : 255)
	)
		.toString(16)
		.slice(1)}`;
	return out;
}

export function darken(color, amount) {
	const num = parseInt(color.replace("#", ""), 16);
	const f = 1 - amount;
	const c = 0 * amount;
	const R = Math.round((num >> 16) * f + c);
	const G = Math.round(((num >> 8) & 0x00ff) * f + c);
	const B = Math.round((num & 0x0000ff) * f + c);
	const out = `#${(
		0x1000000 +
		(R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
		(G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
		(B < 255 ? (B < 1 ? 0 : B) : 255)
	)
		.toString(16)
		.slice(1)}`;
	return out;
}
/* eslint-enable no-bitwise */

export function getStripes(colour = "#B4645D", l = 8, bg = null, rot = false, mirror = false) {
	const patternCanvas = document.createElement("canvas");
	const pctx = patternCanvas.getContext("2d", { antialias: true });

	const CANVAS_SIDE_LENGTH = l;
	const WIDTH = CANVAS_SIDE_LENGTH;
	const HEIGHT = CANVAS_SIDE_LENGTH;
	const DIVISIONS = 4;

	patternCanvas.width = WIDTH;
	patternCanvas.height = HEIGHT;

	if (rot) {
		pctx.translate(WIDTH / 2, HEIGHT / 2);
		pctx.rotate(Math.PI / 2);
	}

	if (bg) {
		pctx.fillStyle = bg;
		pctx.fillRect(0, 0, WIDTH, HEIGHT);
	}

	if (mirror) {
		pctx.translate(WIDTH / 2, HEIGHT / 2);
		pctx.rotate(Math.PI / 2);
		pctx.translate(-WIDTH / 2, -HEIGHT / 2);
	}

	pctx.fillStyle = colour;

	// Top line
	pctx.beginPath();
	pctx.moveTo(0, HEIGHT * (1 / DIVISIONS));
	pctx.lineTo(WIDTH * (1 / DIVISIONS), 0);
	pctx.lineTo(0, 0);
	pctx.lineTo(0, HEIGHT * (1 / DIVISIONS));
	pctx.fill();

	// Middle line
	pctx.beginPath();
	pctx.moveTo(WIDTH, HEIGHT * (1 / DIVISIONS));
	pctx.lineTo(WIDTH * (1 / DIVISIONS), HEIGHT);
	pctx.lineTo(0, HEIGHT);
	pctx.lineTo(0, HEIGHT * ((DIVISIONS - 1) / DIVISIONS));
	pctx.lineTo(WIDTH * ((DIVISIONS - 1) / DIVISIONS), 0);
	pctx.lineTo(WIDTH, 0);
	pctx.lineTo(WIDTH, HEIGHT * (1 / DIVISIONS));
	pctx.fill();

	// Bottom line
	pctx.beginPath();
	pctx.moveTo(WIDTH, HEIGHT * ((DIVISIONS - 1) / DIVISIONS));
	pctx.lineTo(WIDTH * ((DIVISIONS - 1) / DIVISIONS), HEIGHT);
	pctx.lineTo(WIDTH, HEIGHT);
	pctx.lineTo(WIDTH, HEIGHT * ((DIVISIONS - 1) / DIVISIONS));
	pctx.fill();

	return patternCanvas.toDataURL("image/png");
}

export function getDot(colour = "#000000", size = 10) {
	const canvas = document.createElement("canvas");
	const ctx = canvas.getContext("2d", { antialias: true });

	canvas.width = size;
	canvas.height = size;

	ctx.beginPath();
	ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI, false);
	ctx.fillStyle = colour;
	ctx.fill();

	return canvas.toDataURL("image/png");
}

// haversine
export function distance(lat1, lon1, lat2, lon2, unit) {
	if (lat1 === lat2 && lon1 === lon2) {
		return 0;
	}

	const radlat1 = (Math.PI * lat1) / 180;
	const radlat2 = (Math.PI * lat2) / 180;
	const theta = lon1 - lon2;
	const radtheta = (Math.PI * theta) / 180;
	let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
	if (dist > 1) {
		dist = 1;
	}
	dist = Math.acos(dist);
	dist = (dist * 180) / Math.PI;
	dist = dist * 60 * 1.1515;
	if (unit === "K") {
		dist *= 1.609344;
	}
	if (unit === "N") {
		dist *= 0.8684;
	}
	return dist;
}

export function capitalizeFirstLetter(string) {
	return string.charAt(0).toUpperCase() + string.slice(1);
}
/*
 * FUNCTIONS FROM OUR OLD utils.js
 */

export function stringToDate(_date, _format, _delimiter) {
	const formatLowerCase = _format.toLowerCase();
	const formatItems = formatLowerCase.split(_delimiter);
	const dateItems = _date.split(_delimiter);
	const monthIndex = formatItems.indexOf("mm");
	const dayIndex = formatItems.indexOf("dd");
	const yearIndex = formatItems.indexOf("yyyy");
	return new Date(`${dateItems[yearIndex]}-${dateItems[monthIndex]}-${dateItems[dayIndex]}`);
}

export function dateToString(date) {
	const dateString = [
		date.getFullYear(),
		String(date.getMonth() + 1).padStart(2, "0"),
		String(date.getDate()).padStart(2, "0"),
	].join("-");
	return dateString;
}

const locale = formatLocale({
	decimal: ",",
	thousands: ".",
	grouping: [3],
});

const format = { decimal: ",", thousands: ".", currencyPre: false };

function fixThousandSep(x) {
	const s = x.split(".");
	if (s.length !== 2) return x;
	if (s[0].length !== 1) return x;
	return x.replace(".", "");
}

export const zeroPad = (x) => `00${x}`.slice(-2);

export function formatInt(postfix = "") {
	return (x) => fixThousandSep(locale.format(",d")(x)) + postfix;
}

const formatFloatDefaultOptions = {
	digits: 1,
	postfix: "",
	prefix: "",
	multiplier: 1,
	signed: false,
	decimal: false, // remove trailing zeros
	adaptive: true, // add extra digits for small numbers
};

export function formatFloat(opts) {
	const o = { ...formatFloatDefaultOptions, ...opts };

	return function (x) {
		let ds = o.digits;
		if (x === "" || x === null || x === undefined) return "";
		if (o.adaptive) {
			// D 2021-04-22 removed the following line, to have 12,8 and 0,3 (instead of 0,31)
			// if (Math.abs(x * o.multiplier) < 1) ds += 1;
			if (Math.abs(x * o.multiplier) < 0.1) ds += 1;
			if (Math.abs(x * o.multiplier) < 0.01) ds += 1;
			if (Math.abs(x * o.multiplier) < 0.001) ds += 1;
		}
		let s = locale.format(`,.${ds}f`)(x * o.multiplier);
		if (x === 0) s = s.replace("+", "");
		s = fixThousandSep(s, format.thousands);
		if (o.adaptive && s.replace(/0/g, "").replace(format.decimal, "") === "") s = "0";
		if (o.decimal) {
			s = s.replace(new RegExp(`${format.decimal.replace(".", "\\.")}0*$`), "");
			if (s.indexOf(format.decimal) > -1) {
				s = s.replace(/0*$/, "");
			}
		}
		if (o.signed) {
			const number = +s
				.replace(new RegExp(`${format.thousands.replace(".", "\\.")}`, "g"), "")
				.replace(new RegExp(`${format.decimal.replace(".", "\\.")}`), ".");
			if (number > 0) {
				s = "+" + s;
			} else if (number === 0) {
				s = "±" + s;
			}
		}
		return (o.prefix || "") + s + (o.postfix || "");
	};
}

export function formatFloatK(opts) {
	return formatFloat({
		...opts,
		multiplier: 1e-3,
		postfix: (format.noSpaceBeforeOrder ? "" : "\u00a0") + "Tsd" + (opts.postfix || ""),
	});
}

export function formatFloatM(opts) {
	return formatFloat({
		...opts,
		multiplier: 1e-6,
		postfix: (format.noSpaceBeforeOrder ? "" : "\u00a0") + "Mio" + (opts.postfix || ""),
	});
}

export function formatFloatB(opts) {
	if (format.noB) return formatFloatM(opts);
	return formatFloat({
		...opts,
		multiplier: 1e-9,
		postfix: (format.noSpaceBeforeOrder ? "" : "\u00a0") + "Mrd" + (opts.postfix || ""),
	});
}

export function formatFloatKVerbose(opts) {
	return formatFloat({
		...opts,
		multiplier: 1e-3,
		postfix: `\u00a0${"Tausend"}${opts.postfix || ""}`,
	});
}

export function formatFloatMVerbose(opts) {
	return formatFloat({
		...opts,
		multiplier: 1e-6,
		postfix: `\u00a0${"Millionen"}${opts.postfix || ""}`,
	});
}

export function formatFloatBVerbose(opts) {
	if (format.noB) return formatFloatMVerbose(opts);
	return formatFloat({
		...opts,
		multiplier: 1e-9,
		postfix: `\u00a0${"Milliarden"}${opts.postfix || ""}`,
	});
}

export function formatIntAdaptive(opts = {}) {
	const frmtI = formatInt(opts);
	const frmtM = formatFloatM(opts);
	const frmtB = formatFloatB(opts);
	const frmtK = formatFloatK(opts);

	return function (v) {
		if (v >= 10 ** (9 - opts.digits)) return frmtB(v);
		if (v >= 10 ** (6 - opts.digits)) return frmtM(v);
		if (opts.useK && v >= 10 ** (4 - opts.digits)) return frmtK(v);
		return frmtI(v);
	};
}

export function formatFloatAdaptive(opts = {}) {
	const frmtI = formatFloat(opts);
	const frmtM = formatFloatM(opts);
	const frmtB = formatFloatB(opts);

	return function (v) {
		if (v >= 10 ** (9 - opts.digits) + 1) return frmtB(v);
		if (v >= 10 ** (6 - opts.digits) + 1) return frmtM(v);
		return frmtI(v);
	};
}

export function formatIntAdaptiveVerbose(opts = {}) {
	const frmtI = formatInt(opts);
	const frmtM = formatFloatMVerbose(opts);
	const frmtB = formatFloatBVerbose(opts);

	return function (v) {
		if (v >= 10 ** (9 - opts.digits)) return frmtB(v);
		if (v >= 10 ** (6 - opts.digits)) return frmtM(v);
		return frmtI(v);
	};
}

export function formatFloatAdaptiveVerbose(opts = {}) {
	const frmtI = formatFloat(opts);
	const frmtM = formatFloatMVerbose(opts);
	const frmtB = formatFloatBVerbose(opts);

	return function (v) {
		if (v >= 10 ** (9 - opts.digits)) return frmtB(v);
		if (v >= 10 ** (6 - opts.digits)) return frmtM(v);
		return frmtI(v);
	};
}

const handleCurrency = (opts) => ({
	...opts,
	prefix: (opts.prefix || "") + (format.currencyPre ? opts.currency || "€" : ""),
	postfix: (!format.currencyPre ? `\u2009${opts.currency || "€"}` : "") + (opts.postfix || ""),
});

export function formatMoneyInt(opts = {}) {
	return formatInt(handleCurrency(opts));
}

export function formatMoneyFloat(opts = {}) {
	return formatFloat(handleCurrency(opts));
}

export function formatMoneyFloatK(opts = {}) {
	return formatFloatK(handleCurrency(opts));
}

export function formatMoneyFloatM(opts = {}) {
	return formatFloatM(handleCurrency(opts));
}

export function formatMoneyFloatB(opts = {}) {
	return formatFloatB(handleCurrency(opts));
}

export function formatMoneyIntAdaptive(opts = {}) {
	return formatIntAdaptive(handleCurrency(opts));
}

export function formatMoneyFloatAdaptive(opts = {}) {
	return formatFloatAdaptive(handleCurrency(opts));
}

export function formatMoneyFloatKVerbose(opts = {}) {
	return formatFloatKVerbose(handleCurrency(opts));
}

export function formatMoneyFloatMVerbose(opts = {}) {
	return formatFloatMVerbose(handleCurrency(opts));
}

export function formatMoneyFloatBVerbose(opts = {}) {
	return formatFloatBVerbose(handleCurrency(opts));
}

export function formatMoneyIntAdaptiveVerbose(opts = {}) {
	return formatIntAdaptiveVerbose(handleCurrency(opts));
}

export function formatMoneyFloatAdaptiveVerbose(opts = {}) {
	return formatFloatAdaptiveVerbose(handleCurrency(opts));
}

export function formatDayMonth(date) {
	const day = date.getDate();
	const monthIndex = date.getMonth();
	// const year = date.getFullYear();
	return `${day}.${monthIndex + 1}.`;
}

export function parseDayToDate(s) {
	return stringToDate(s, "dd.mm.yyyy", ".");
}

export function parseDayToDateUS(s) {
	return stringToDate(s, "yyyy-mm-dd", "-");
}

export function parseSinceToDate(s) {
	return new Date(+s);
}

export function daysBetween(date1, date2) {
	// The number of milliseconds in one day
	const ONE_DAY = 1000 * 60 * 60 * 24;
	// Calculate the difference in milliseconds
	const differenceMs = Math.abs(date1 - date2);
	// Convert back to days and return
	return Math.round(differenceMs / ONE_DAY);
}

export function dateSeriesToSinceSeries(s, dateMin) {
	const res = [];
	s.forEach((d) => {
		if (+d.date >= +dateMin) {
			const days = daysBetween(d.date, dateMin);
			d.dateOrig = d.date;
			d.dateStringOrig = d.dateString;
			d.date = new Date(days);
			d.dateString = `${days} ${days === 1 ? "Tag" : "Tage"} (${d.dateStringOrig})`;
			res.push(d);
		}
	});
	return res;
}

export function copy(ds) {
	return JSON.parse(JSON.stringify(ds));
}

export function sortSeries(ds) {
	ds.sort((a, b) =>
		descending(
			(a.series[a.series.length - 1] || { value: 0 }).value,
			(b.series[b.series.length - 1] || { value: 0 }).value
		)
	);
	return ds;
}

export function toFront(array, what) {
	if (array.indexOf(what) > 0) {
		array.splice(array.indexOf(what), 1);
		array.unshift(what);
	}
}

export function sortSeriesByOrder(ds, order) {
	ds.sort((a, b) => ascending(order.indexOf(a.name), order.indexOf(b.name)));
	return ds;
}

export function parseQuarterToDate(s) {
	// e.g. 2019-Q4
	return new Date(s.replace("Q1", "03-31").replace("Q2", "06-30").replace("Q3", "09-30").replace("Q4", "12-31"));
}

export function parseYearToDate(s) {
	return new Date(s);
}

export function parseMonthToDate(s) {
	const x = s.split(".");
	const month = x[0];
	const year = x[1];
	return new Date(`${year}-${month}`);
}

export function getYears(first, last, step) {
	const ys = [];
	for (let y = first; y <= last; y += step || 1) {
		ys.push(`${y}`);
	}
	return ys;
}

export function findIndexWithAttr(array, attr, value) {
	for (let i = 0; i < array.length; i += 1) {
		if (array[i][attr] === value) {
			return i;
		}
	}
	return null;
}

const monthNames = [
	"Januar",
	"Februar",
	"März",
	"April",
	"Mai",
	"Juni",
	"Juli",
	"August",
	"September",
	"Oktober",
	"November",
	"Dezember",
];

export const monthNamesShort = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

export function formatDay(date) {
	const day = date.getDate();
	const monthIndex = date.getMonth();
	const year = date.getFullYear();
	return `${zeroPad(day)}.${zeroPad(monthIndex + 1)}.${year}`;
}

export function formatDayShort(date) {
	const day = date.getDate();
	const monthIndex = date.getMonth();
	return `${day}.${monthIndex + 1}.`;
}

export function formatDayISO(date) {
	const day = date.getDate();
	const monthIndex = date.getMonth();
	const year = date.getFullYear();
	return `${year}-${zeroPad(monthIndex + 1)}-${zeroPad(day)}`;
}

/*
export function formatMonth(date) {
  const monthIndex = date.getMonth();
  const year = date.getFullYear();
  return `${monthNames[monthIndex]} ${year}`;
}
*/
export function formatMonth(date) {
	const monthIndex = date.getMonth();
	const year = date.getFullYear();
	return `${zeroPad(monthIndex + 1)}.${year}`;
}

export function formatOnlyMonth(date) {
	const monthIndex = date.getMonth();
	return monthNamesShort[monthIndex];
}

export function formatOnlyMonthLong(date) {
	const monthIndex = date.getMonth();
	return monthNames[monthIndex];
}

export function formatYear(date) {
	const year = date.getFullYear();
	return `${year}`;
}

export function formatSince(what) {
	return function (date) {
		return `${date.getTime()}${what}`;
	};
}

export function formatQuarter(date) {
	const month = date.getMonth() + 1;
	const q = Math.ceil(month / 3);
	const year = date.getFullYear();
	return `Q${q} ${year}`;
}

const weekdays = ["SO", "MO", "DI", "MI", "DO", "FR", "SA"];

export function formatDayWeekday(date) {
	const wday = date.getDay();
	const day = date.getDate();
	const monthIndex = date.getMonth();
	const year = date.getFullYear();
	return `${weekdays[wday]} ${zeroPad(day)}.${zeroPad(monthIndex + 1)}.${year}`;
}

export function getMultiplierAndPostfix(n) {
	if (n >= 1e9) return [1e-9, " Mrd."];
	if (n >= 1e6) return [1e-6, " Mio."];
	if (n >= 1e4) return [1e-3, " Tsd."];
	return [1, ""];
}

export function keepOnlyFirstPostfix(d, i) {
	if (i === 0) return d;
	return d.split(" ")[0];
}

export function slugify(x) {
	const step1 = x.replace(/^[^-_a-zA-Z]+/, "").replace(/^-(?:[-0-9]+)/, "-");
	const step2 = step1 && step1.replace(/[^-_a-zA-Z0-9]+/g, "-");
	return step2;
}

export function transpose(ds) {
	const out = [];
	const dates = Object.keys(ds[0]).filter((k) => k !== "name");
	const names = ds.map((d) => d.name);
	const dsByName = {};
	ds.forEach((d) => {
		dsByName[d.name] = d;
	});
	dates.forEach((dt) => {
		const dnew = { date: dt };
		names.forEach((n) => {
			dnew[n] = dsByName[n][dt];
		});
		out.push(dnew);
	});
	return out;
}

export function fillNaNs(data) {
	const keys = Object.keys(data[0]).filter((k) => k !== "name");
	data.forEach((d) => {
		keys.forEach((k) => {
			if (d[k] === "") d[k] = "-";
		});
	});
	return data;
}

export function fillNaNsJSON(data) {
	Object.keys(data).forEach((k) => {
		if (data[k] === "") data[k] = "-";
	});
	return data;
}

export function filterNaNs(ds) {
	ds.forEach((dat) => {
		dat.series = dat.series.filter((d) => Number.isFinite(d.value));
	});
	return ds;
}

export function filterNoData(ds) {
	ds.forEach((dat) => {
		if (dat.series.length < 1) dat.noData = true;
	});
	return ds;
}

export function parseDay(d) {
	const ret = {};
	Object.keys(d).forEach((k) => {
		if (k === "date") {
			ret.date = parseDayToDate(d.date);
			ret.dateString = d.date;
		} else {
			ret[k] = +d[k];
		}
	});
	return ret;
}

export function parseDayUS(d) {
	const ret = {};
	Object.keys(d).forEach((k) => {
		if (k === "date") {
			ret.date = parseDayToDateUS(d.date);
			ret.dateString = d.date;
		} else {
			ret[k] = +d[k];
		}
	});
	return ret;
}

export function parseYear(d) {
	const ret = {};
	Object.keys(d).forEach((k) => {
		if (k === "date") {
			ret.date = parseDayToDate(`01.01.${d.date}`);
			ret.dateString = d.date;
		} else {
			ret[k] = +d[k];
		}
	});
	return ret;
}

export function parseMonth(d) {
	const ret = {};
	Object.keys(d).forEach((k) => {
		if (k === "date") {
			ret.date = parseDayToDate(`01.${d.date}`);
			ret.dateString = d.date;
		} else {
			ret[k] = +d[k];
		}
	});
	return ret;
}

export function parseOnlyMonth(d) {
	const ret = {};
	Object.keys(d).forEach((k) => {
		if (k === "date") {
			ret.date = parseDayToDate(`01.${zeroPad(d.date)}.2000`);
			ret.dateString = d.date;
		} else {
			ret[k] = +d[k];
		}
	});
	return ret;
}

export function splitData(data) {
	const out = [];
	Object.keys(data[0]).forEach((k) => {
		if (k !== "date" && k !== "dateString") {
			const series = data.map((d) => ({
				date: d.date,
				dateString: d.dateString,
				value: d[k],
			}));
			series.sort((a, b) => ascending(a.date, b.date));
			out.push({
				name: k,
				slug: slugify(k),
				series,
			});
		}
	});
	return out;
}

export function splitDataCI(data) {
	const out = [];
	Object.keys(data[0]).forEach((k) => {
		if (k !== "date" && k !== "dateString" && k !== "institute" && !k.endsWith("_ciu") && !k.endsWith("_cil")) {
			const series = data.map((d) => ({
				date: d.date,
				dateString: d.dateString,
				value: d[k],
				institute: d.institute,
				ciu: d[`${k}_ciu`],
				cil: d[`${k}_cil`],
			}));
			series.sort((a, b) => ascending(a.date, b.date));
			out.push({
				name: k,
				slug: slugify(k),
				series,
			});
		}
	});
	return out;
}

const log10 =
	Math.log10 ||
	function (x) {
		return Math.log(x) * Math.LOG10E;
	};

export function range(start, end) {
	return Array(end - start + 1)
		.fill()
		.map((_, idx) => start + idx);
}

const NTICKS = 6;

export function getTicksY(maxValue, nticks = NTICKS) {
	const p = Math.floor(log10(maxValue)); // get order of magnitude
	let n = Math.ceil(maxValue / 10 ** p) - 1;
	let delta = 10 ** p;
	if (n > nticks) {
		n = Math.ceil(n / 2);
		delta *= 2;
	}
	if (n > nticks) {
		n = Math.ceil(n / 2);
		delta *= 2;
	}
	if (n <= nticks) {
		n *= 2;
		delta /= 2;
	}
	if (n > nticks + 1) {
		n = Math.ceil(n / 2);
		delta *= 2;
	}
	// if (n * delta > max) n -= 1;
	// if ((n + 1) * delta < max) n += 1;
	if ((n + 1) * delta >= maxValue) n -= 1;
	if ((n + 1) * delta >= maxValue) n -= 1;
	if (n > nticks - 1) {
		n = Math.ceil(n / 2);
		delta *= 2;
	}
	return range(1, n + 1)
		.map((i) => i * delta)
		.reverse();
}

export function getTicksXdays(max, step = 7) {
	const n = Math.floor(+max / step);
	return range(0, n).map((i) => i * step);
}

const FACTOR = 10;

export function getTicksYlog(maxValue, min) {
	const ticks = getTicksY(maxValue);
	const ticksMax = ticks[ticks.length - 1];
	const out = [ticksMax];
	let n = ticksMax / FACTOR;
	while (n > (min || 0.1)) {
		out.push(n);
		n /= FACTOR;
	}
	return out;
}

export function extendTicksYlog(ticks, n = 5) {
	const res = [];
	for (let i = 0; i < ticks.length - 1; i++) {
		const d = (ticks[i + 1] - ticks[i]) / n;
		range(0, n)
			.map((x) => ticks[i] + d * x)
			.forEach((x) => {
				res.push(x);
			});
	}
	res.push(ticks[ticks.length - 1]);
	return res;
}

export function overlaps(rect1, rect2) {
	return !(
		rect1.right < rect2.left ||
		rect1.left > rect2.right ||
		rect1.bottom < rect2.top ||
		rect1.top > rect2.bottom
	);
}

export function isMobileSafari() {
	const ua = window.navigator.userAgent;
	const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
	const webkit = !!ua.match(/WebKit/i);
	const iOSSafari = iOS && webkit && !ua.match(/CriOS/i);
	return iOSSafari;
}

export function isDeviceNoAnimation() {
	// maxTouchPoints: b/c we cannot detect iPads..
	// return isMobileSafari() || navigator.maxTouchPoints > 1 || window.innerWidth < 600;
	// return isMobileSafari() || navigator.maxTouchPoints > 1 || window.innerWidth < 600;
	return false;
}

export const DISABLE_ANIMATIONS_ON_MOBILE = false;

let pSBCr;
// https://stackoverflow.com/a/13542669
export const pSBC = (p, c0, c1, l) => {
	/* eslint-disable */
	let r,
		g,
		b,
		P,
		f,
		t,
		h,
		i = parseInt,
		m = Math.round,
		a = typeof c1 == "string";
	if (typeof p != "number" || p < -1 || p > 1 || typeof c0 != "string" || (c0[0] != "r" && c0[0] != "#") || (c1 && !a))
		return null;
	if (!pSBCr)
		pSBCr = (d) => {
			let n = d.length,
				x = {};
			if (n > 9) {
				([r, g, b, a] = d = d.split(",")), (n = d.length);
				if (n < 3 || n > 4) return null;
				(x.r = i(r[3] == "a" ? r.slice(5) : r.slice(4))), (x.g = i(g)), (x.b = i(b)), (x.a = a ? parseFloat(a) : -1);
			} else {
				if (n == 8 || n == 6 || n < 4) return null;
				if (n < 6) d = "#" + d[1] + d[1] + d[2] + d[2] + d[3] + d[3] + (n > 4 ? d[4] + d[4] : "");
				d = i(d.slice(1), 16);
				if (n == 9 || n == 5)
					(x.r = (d >> 24) & 255), (x.g = (d >> 16) & 255), (x.b = (d >> 8) & 255), (x.a = m((d & 255) / 0.255) / 1000);
				else (x.r = d >> 16), (x.g = (d >> 8) & 255), (x.b = d & 255), (x.a = -1);
			}
			return x;
		};
	(h = c0.length > 9),
		(h = a ? (c1.length > 9 ? true : c1 == "c" ? !h : false) : h),
		(f = pSBCr(c0)),
		(P = p < 0),
		(t = c1 && c1 != "c" ? pSBCr(c1) : P ? { r: 0, g: 0, b: 0, a: -1 } : { r: 255, g: 255, b: 255, a: -1 }),
		(p = P ? p * -1 : p),
		(P = 1 - p);
	if (!f || !t) return null;
	if (l) (r = m(P * f.r + p * t.r)), (g = m(P * f.g + p * t.g)), (b = m(P * f.b + p * t.b));
	else
		(r = m((P * f.r ** 2 + p * t.r ** 2) ** 0.5)),
			(g = m((P * f.g ** 2 + p * t.g ** 2) ** 0.5)),
			(b = m((P * f.b ** 2 + p * t.b ** 2) ** 0.5));
	(a = f.a), (t = t.a), (f = a >= 0 || t >= 0), (a = f ? (a < 0 ? t : t < 0 ? a : a * P + t * p) : 0);
	if (h) return "rgb" + (f ? "a(" : "(") + r + "," + g + "," + b + (f ? "," + m(a * 1000) / 1000 : "") + ")";
	else
		return (
			"#" +
			(4294967296 + r * 16777216 + g * 65536 + b * 256 + (f ? m(a * 255) : 0)).toString(16).slice(1, f ? undefined : -2)
		);
};

/* AUTOMATIC X-TICKS */

/* | Jan | Feb | Mär | ... */

export function getXTicksMonths(start, end, addIncompleteMonth) {
	let s = new Date(+start);
	let e = new Date(+end);
	let r = [];

	if (addIncompleteMonth && e.getDate() >= 20) e.setDate(e.getDate() + 12);
	if (addIncompleteMonth && s.getDate() <= 10) s.setDate(s.getDate() - 12);
	while (+s <= +e) {
		if (s.getDate() === 1) r.push(new Date(+s));
		s.setDate(s.getDate() + 1);
	}

	return r;
}

export function getXTicksYears(start, end) {
	let s = new Date(+start);
	let e = new Date(+end);
	let r = [];

	while (+s <= +e) {
		r.push(new Date(+s));
		s.setFullYear(s.getFullYear() + 1);
	}
	return r;
}

export function getXTicksLabelsPositionsMonths(start, end) {
	const ticks = getXTicksMonths(start, end, true);
	let r = [];
	for (let i = 0; i < ticks.length - 1; i++) {
		r.push(new Date(+ticks[i] + (+ticks[i + 1] - +ticks[i]) / 2));
	}
	return r;
}

export function getXTicksLabelsMonths(start, end) {
	const ticks = getXTicksMonths(start, end, true);
	let r = [];
	for (let i = 0; i < ticks.length - 1; i++) {
		r.push(monthNamesShort[ticks[i].getMonth()]);
	}
	return r;
}

/* |1.1.  |1.2.  |1.3  |1.4 */

export function getXTicksMonthsFirst(start, end) {
	return getXTicksMonths(start, end);
}

export function getXTicksLabelsPositionsMonthsFirst(start, end) {
	return getXTicksMonths(start, end);
}

export function getXTicksLabelsMonthsFirst(start, end) {
	return getXTicksMonths(start, end).map(formatDayShort);
}

/* |1.1. ||||...  |1.2. ||||.... |1.3 ||||...  |1.4 |.... */

export function getXTicksDays(start, end) {
	let s = new Date(+start);
	let e = new Date(+end);
	let r = [];
	while (+s <= +e) {
		r.push(new Date(+s));
		s.setDate(s.getDate() + 1);
	}
	return r;
}

export function getXTicksMonthsDays(start, end) {
	return getXTicksDays(start, end);
}

export function getXTicksLabelsPositionsMonthsDays(start, end) {
	return getXTicksMonths(start, end);
}

export function getXTicksLabelsMonthsDays(start, end) {
	return getXTicksMonths(start, end).map(formatDayShort);
}

/* |<start> |||.... |<end> */

export function getXTicksRange(start, end) {
	return getXTicksDays(start, end);
}

export function getXTicksLabelsPositionsRange(start, end) {
	return [start, end];
}

export function getXTicksLabelsRange(start, end) {
	return [start, end].map(formatDayShort);
}

/* automatically choose a good variant */

export function getXTicksSmart(start, end) {
	const d = daysBetween(start, end);
	if (d > 280) return getXTicksMonthsFirst(start, end);
	if (d > 100) return getXTicksMonths(start, end);
	//if (d > 60) return getXTicksMonthsDays(start, end);
	if (d > 60) return getXTicksMonthsFirst(start, end);
	return getXTicksRange(start, end);
}

export function getXTicksLabelsSmart(start, end) {
	const d = daysBetween(start, end);
	if (d > 280) return getXTicksLabelsMonthsFirst(start, end);
	if (d > 100) return getXTicksLabelsMonths(start, end);
	//if (d > 60) return getXTicksLabelsMonthsDays(start, end);
	if (d > 60) return getXTicksLabelsMonthsFirst(start, end);
	return getXTicksLabelsRange(start, end);
}

export function getXTicksLabelsPositionsSmart(start, end) {
	const d = daysBetween(start, end);
	if (d > 280) return getXTicksLabelsPositionsMonthsFirst(start, end);
	if (d > 100) return getXTicksLabelsPositionsMonths(start, end);
	//if (d > 60) return getXTicksLabelsPositionsMonthsDays(start, end);
	if (d > 60) return getXTicksLabelsPositionsMonthsFirst(start, end);
	return getXTicksLabelsPositionsRange(start, end);
}

/* start plus end date plus start of months in between */
export function getXTicksStartEndMonths(start, end) {
	let s = new Date(+start);
	let st = new Date(+start);
	let e = new Date(+end);
	let en = new Date(+end);
	let r = [st];

	e.setDate(e.getDate() - 27);
	if (s.getDate() > 5) s.setDate(s.getDate() + 27);
	while (+s < +e) {
		if (s.getDate() === 1) r.push(new Date(+s));
		s.setDate(s.getDate() + 1);
	}
	r.push(en);

	return r;
}

export function convertToGeoJSON(topojsonData) {
	const topojson = window.topojson;

	return topojson.feature(topojsonData, topojsonData.objects[Object.keys(topojsonData.objects)[0]]);
}

export const groupBy = function (xs, key) {
	return xs.reduce(function (rv, x) {
		(rv[x[key]] = rv[x[key]] || []).push(x);
		return rv;
	}, {});
};
